import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileUp, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import type { TermReviewIssueKind } from "@/api/db/connection";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { OverlayShell } from "@/components/ui/overlay";
import { SkeletonRows } from "@/components/ui/skeleton";
import {
	useGenerationCancel,
	useGenerationCreate,
	useGenerationRetry,
	useGenerationStatus,
} from "@/hooks/use-terms";
import { BrowserFile } from "@/lib/browser-file";
import { TermosLabels } from "@/lib/labels/termos";

const PDF_CEILING_BYTES = 500 * 1024;

const labels = TermosLabels.generation;

export function GenerateTermModal({
	initialId,
	onClose,
}: {
	initialId: number | null;
	onClose(): void;
}) {
	const [id, setId] = useState(initialId);

	return (
		<OverlayShell
			label={labels.title}
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<div data-slot="generate-term-modal" className="overlay-panel max-w-lg gap-4 p-5">
				<h2 className="type-section-title text-foreground">{labels.title}</h2>

				{id === null && <UploadStage onCreated={setId} />}
				{id !== null && <TrackingStage id={id} onClose={onClose} />}
			</div>
		</OverlayShell>
	);
}

function UploadStage({ onCreated }: { onCreated(id: number): void }) {
	const inputRef = useRef<HTMLInputElement>(null);
	const create = useGenerationCreate();

	const onPick = async (file: File) => {
		if (file.type !== "application/pdf") {
			toast.error(labels.notPdf);

			return;
		}

		if (file.size > PDF_CEILING_BYTES) {
			toast.error(labels.tooLarge);

			return;
		}

		const data = await BrowserFile.toBase64(file);

		create.mutate({ filename: file.name, data }, { onSuccess: (result) => onCreated(result.id) });
	};

	return (
		<div className="flex flex-col gap-3">
			<p className="text-xs text-muted-foreground">{labels.uploadHint}</p>

			<input
				ref={inputRef}
				type="file"
				accept="application/pdf"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];

					if (file) {
						void onPick(file);
					}

					event.target.value = "";
				}}
			/>

			<button
				type="button"
				data-slot="generate-term-pick"
				disabled={create.isPending}
				onClick={() => inputRef.current?.click()}
				className="flex flex-col items-center gap-2 rounded-md border border-dashed border-primary/30 bg-primary/[0.03] px-4 py-8 text-primary transition-colors hover:bg-primary/[0.06] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
			>
				{create.isPending && <Loader2 className="size-5 animate-spin" />}
				{!create.isPending && <FileUp className="size-5" />}

				<span className="text-sm font-medium">{labels.pick}</span>
				<span className="text-xs text-muted-foreground">{labels.constraints}</span>
			</button>
		</div>
	);
}

function TrackingStage({ id, onClose }: { id: number; onClose(): void }) {
	const status = useGenerationStatus(id);
	const cancel = useGenerationCancel();
	const retry = useGenerationRetry();
	const queryClient = useQueryClient();

	const state = status.data?.state;

	useEffect(() => {
		if (state === "done") {
			void queryClient.invalidateQueries({ queryKey: orpc.terms.list.key() });
		}
	}, [state, queryClient]);

	if (status.isError) {
		return (
			<p className="text-sm text-muted-foreground" data-slot="generate-term-canceled">
				{labels.canceled}
			</p>
		);
	}

	if (status.data === undefined) {
		return <SkeletonRows rows={3} />;
	}

	const generation = status.data;

	if (generation.state === "done") {
		return (
			<div className="flex flex-col gap-3" data-slot="generate-term-done">
				<p className="text-sm text-foreground">
					{labels.done} <strong>{generation.name}</strong>
				</p>

				{generation.incomplete && (
					<p className="flex items-start gap-1.5 text-xs text-warning">
						<TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
						{labels.doneIncomplete}
					</p>
				)}

				<GenerationIssues issues={generation.issues} />

				<div className="flex items-center justify-end">
					<Button size="sm" asChild data-slot="generate-term-open">
						<Link to="/clinica/termo/$id" params={{ id }} onClick={onClose}>
							{labels.openEditor}
						</Link>
					</Button>
				</div>
			</div>
		);
	}

	if (generation.state === "failed") {
		return (
			<div className="flex flex-col gap-3" data-slot="generate-term-failed">
				<p className="flex items-center gap-1.5 text-sm text-destructive">
					<TriangleAlert className="size-4 shrink-0" aria-hidden />
					{labels.failed}
				</p>

				<div className="flex items-center justify-end gap-2">
					<Button
						size="sm"
						variant="ghost"
						data-slot="generate-term-discard"
						className="text-destructive hover:bg-destructive/[0.07] hover:text-destructive"
						disabled={cancel.isPending || retry.isPending}
						onClick={() => cancel.mutate({ id })}
					>
						{labels.discard}
					</Button>

					<Button
						size="sm"
						data-slot="generate-term-retry"
						disabled={retry.isPending || cancel.isPending}
						onClick={() => retry.mutate({ id })}
					>
						{labels.retry}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3" data-slot="generate-term-tracking">
			<p className="flex items-center gap-2 text-sm text-foreground">
				<Loader2 className="size-4 animate-spin text-primary" />
				{labels.phase(generation.phase)}

				{generation.blocksTotal > 0 && (
					<span className="font-mono text-xs text-muted-foreground">
						{labels.blocks(generation.blocksTotal)}
					</span>
				)}
			</p>

			{generation.blocksTotal > 0 && (
				<img
					src={`/term-generation/preview/${id}?v=${generation.stamp}`}
					alt={labels.previewAlt}
					className="max-h-[50dvh] w-full rounded-md border border-border object-contain"
				/>
			)}

			<p className="text-xs text-muted-foreground">{labels.trackingHint}</p>

			<div className="flex items-center justify-end">
				<Button
					type="button"
					variant="secondary"
					size="sm"
					data-slot="generate-term-cancel"
					disabled={cancel.isPending || cancel.isSuccess}
					onClick={() => cancel.mutate({ id })}
				>
					{labels.cancel}
				</Button>
			</div>
		</div>
	);
}

function GenerationIssues({ issues }: { issues: { kind: TermReviewIssueKind; detail: string }[] }) {
	if (issues.length === 0) {
		return null;
	}

	return (
		<div className="rounded-md bg-warning-soft px-3 py-2.5">
			<p className="text-xs font-semibold text-foreground">{labels.issues}</p>

			<ul className="mt-1 list-disc pl-4 text-xs text-foreground/80">
				{issues.map((issue) => (
					<li key={`${issue.kind}:${issue.detail}`}>
						{TermosLabels.review.kinds[issue.kind]}: {issue.detail}
					</li>
				))}
			</ul>
		</div>
	);
}
